import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { toast } from '../hooks/useToast';
import { getErrorMessage } from '../lib/api';

// Shown instead of the app to a user created by their first Microsoft sign-in
// until an account admin grants access (ENG-746). The API refuses everything
// except GET /users/current for them, so nothing else is loaded here.
export const AccessPending = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const refreshUser = useAuthStore((state) => state.refreshUser);
  const logout = useAuthStore((state) => state.logout);
  const [isChecking, setIsChecking] = useState(false);

  const checkAgain = async () => {
    setIsChecking(true);
    try {
      await refreshUser();
      if (!useAuthStore.getState().user?.access_pending) {
        toast.success('You have access now.');
      } else {
        toast.info('Still waiting for an admin to give you access.');
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsChecking(false);
    }
  };

  const signOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <main className="max-w-md w-full space-y-6 text-center">
        <div className="flex justify-center">
          <div className="bg-primary-600 text-white rounded-full p-3">
            <Clock className="h-8 w-8" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Waiting for access</h1>
        <p className="text-gray-600">
          You're signed in{user?.email ? ` as ${user.email}` : ''}, but an Axis360 admin
          still needs to give you access. Ask your manager or the Technology team to set
          you up.
        </p>
        <div className="flex flex-col gap-3">
          <button type="button" onClick={checkAgain} disabled={isChecking} className="w-full btn btn-primary">
            {isChecking ? 'Checking...' : 'Check again'}
          </button>
          <button type="button" onClick={signOut} className="w-full btn btn-secondary">
            Sign out
          </button>
        </div>
      </main>
    </div>
  );
};

export default AccessPending;
